/* eslint-disable max-len */
/* eslint-disable indent */
import Modal from "react-bootstrap/Modal";
import {
  calcularImporteNetoIntermediario,
  calcularComisionEdificiosTotal,
  calcularPorcentajeComisionEdificio,
  resolverDesgloseComision,
  resolverFechaFactura,
} from "../../utils";
import { getApxMonto } from "../../utils/apx";
import "./BuildingDetailsModal.scss";

/**
 * Modal "DETALLE CÁLCULO" — desglose por edificio de una venta.
 * Extraído de VentasFacturasEjecutivo (era una closure interna). El cálculo vive en
 * comisionEdificiosUtil.js (regla 10.75): este componente solo presenta.
 *
 * Props:
 *  - opportunity: venta CRUDA (importe bruto, moneda_dolar, edificiosparticipantes, pantallastotales)
 *  - buildings:   map { [id]: edificio } (edificios vivos)
 *  - clientes / intermediarios: maps (se les aplica Object.values internamente)
 *  - pais, getRate, moneda, localString, decimales
 *  - edificiosVisibles: array de ids del/los edificio(s) del usuario, o null/undefined.
 *      null/undefined → modo completo (interno sin restricción): ve % y costo de todas las filas + TOTAL.
 *      array → modo recortado (usuario edificio): % y costo solo en sus filas ("—" en el resto),
 *      y TOTAL % / TOTAL costo ocultos ("—") para no revelar el agregado de terceros.
 *      Pantallas e inversión neta (por fila y TOTAL) siempre visibles.
 *  - onClose
 */
export const BuildingDetailsModal = ({
  opportunity,
  buildings,
  clientes,
  intermediarios,
  pais,
  snapMap = null,
  pctMap = null,
  takeLocal = 0,
  getRate,
  moneda,
  localString,
  decimales,
  edificiosVisibles = null,
  onClose,
}) => {
  // Comparación por id numérico (regla 10.7). Set para lookup O(1) por fila.
  const visiblesSet = edificiosVisibles ? new Set(edificiosVisibles.map(Number)) : null;
  let buildingDetails = [];
  if (opportunity.edificiosparticipantes && typeof opportunity.edificiosparticipantes === "string") {
    // Convertir a moneda local para cálculos de comisión
    const r = getRate(opportunity);
    const localOpp = opportunity.moneda_dolar
      ? { ...opportunity, importe: (parseFloat(opportunity.importe) || 0) * r, moneda_dolar: false }
      : opportunity;
    const netoParaEdificios = calcularImporteNetoIntermediario(
      localOpp,
      Object.values(clientes),
      Object.values(intermediarios),
      pais,
      pctMap
    );
    // APX (Publicis): auto-resuelto desde localOpp (ya en moneda local) + intermediarios → apxLocal local.
    const apxLocal = getApxMonto(localOpp, intermediarios, pctMap);
    // FASE 6 + APX: take programático y APX bajan la base de edificios (off-the-top, antes del prorrateo).
    const baseEdificios = Math.max(0, netoParaEdificios - (takeLocal || 0) - (apxLocal || 0));
    const totalComisionRounded = calcularComisionEdificiosTotal(
      localOpp,
      buildings,
      netoParaEdificios,
      snapMap,
      takeLocal,
      apxLocal
    );
    const porcentajeTotal = baseEdificios > 0 ? ((totalComisionRounded / baseEdificios) * 100).toFixed(1) : "0.0";

    const edificiosParticipantesUnicos = [...new Set(opportunity.edificiosparticipantes.split(",").map(Number))];

    let totalPantallasSum = 0;
    let totalInvestment = 0;

    buildingDetails = edificiosParticipantesUnicos
      .map((buildingId) => {
        const building = buildings[buildingId];
        if (!building) return null;

        // Display (Fase 2): pantallas y proporción de la MISMA foto que la comisión (resolverDesgloseComision).
        const comisionPercent = calcularPorcentajeComisionEdificio(building, resolverFechaFactura(opportunity));
        const desg = resolverDesgloseComision(opportunity, building, snapMap);
        const pantallasEdificio = desg.numerador;
        const proporcion = desg.denominador > 0 ? desg.numerador / desg.denominador : 0;
        const inversionNeta = baseEdificios * proporcion;
        const comisionEdificio = inversionNeta * (comisionPercent / 100);
        totalPantallasSum += pantallasEdificio;
        totalInvestment += inversionNeta;

        return {
          id: building.id,
          name: building.edificio || "Unknown",
          comision: Math.round(comisionEdificio).toFixed(0),
          percent: comisionPercent.toFixed(1),
          pantallas: pantallasEdificio,
          importeInversion: inversionNeta.toFixed(0),
          // % y costo de comisión: visibles solo si es edificio del usuario (o modo completo).
          comisionVisible: !visiblesSet || visiblesSet.has(Number(building.id)),
          esTotal: false,
        };
      })
      .filter(Boolean);

    buildingDetails.sort((a, b) => a.name.localeCompare(b.name));

    buildingDetails.push({
      id: "__total__",
      name: "Total",
      comision: totalComisionRounded.toFixed(0),
      percent: porcentajeTotal,
      pantallas: totalPantallasSum,
      importeInversion: totalInvestment.toFixed(0),
      // TOTAL % / TOTAL costo: ocultos en modo recortado (revelan el agregado de terceros).
      comisionVisible: !visiblesSet,
      esTotal: true,
    });
  }

  return (
    <Modal show={!!opportunity} onHide={onClose} dialogClassName="wide-modal" centered>
      <Modal.Header closeButton>
        <Modal.Title>DETALLE CÁLCULO</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {buildingDetails.length > 0 ? (
          <div style={{ overflowX: "auto" }}>
            <table className="table table-striped">
              <thead>
                <tr>
                  <th>EDIFICIOS INVOLUCRADOS</th>
                  <th>PANTALLAS</th>
                  <th>INVERSIÓN NETA POR EDIFICIO</th>
                  <th>COMISIÓN DEL EDIFICIO</th>
                  <th>COSTO DE LA COMISIÓN</th>
                </tr>
              </thead>
              <tbody>
                {buildingDetails.map((detail, index) => (
                  <tr key={index} className={detail.name === "Total" ? "table-info" : ""}>
                    <td className="text-start">{detail.name.toUpperCase()}</td>
                    <td className="text-center">{detail.pantallas}</td>
                    <td className="text-end">{`${moneda.toUpperCase()} ${Number(detail.importeInversion).toLocaleString(
                      localString,
                      {
                        maximumFractionDigits: decimales,
                        minimumFractionDigits: decimales,
                      }
                    )}`}</td>
                    <td className="text-center">
                      {!detail.comisionVisible
                        ? "—"
                        : detail.percent
                          ? `${Number(detail.percent).toLocaleString(localString, {
                              maximumFractionDigits: 1,
                              minimumFractionDigits: 1,
                            })}%`
                          : "-"}
                    </td>
                    <td className="text-end">
                      {!detail.comisionVisible
                        ? "—"
                        : `${moneda.toUpperCase()} ${Number(detail.comision).toLocaleString(localString, {
                            maximumFractionDigits: decimales,
                            minimumFractionDigits: decimales,
                          })}`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p>NO HAY DETALLES DE EDIFICIOS DISPONIBLES PARA ESTA OPORTUNIDAD.</p>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button className="btn btn-secondary" onClick={onClose}>
          CERRAR
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default BuildingDetailsModal;
